import { forwardRef } from 'react';
import type { Ref } from 'react';

import type { FontWeight } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { isWeb, styled, Text as TamaguiText } from '@tamagui/core';
import type { GetProps, TamaguiTextElement } from '@tamagui/core';

import type { WithoutPressableProps } from '../../helpers/a11yprops';
import { ComponentWithViewTracking } from '../../helpers/analytics';
import type { AnalyticsProps } from '../../helpers/analytics';

const BaseText = styled(TamaguiText, {
  name: 'BaseText',
  fontFamily: '$body',
  color: '$contentPrimary',
  variants: {
    typography: {
      '...fontSize': (fontSize) => ({
        fontSize,
        fontWeight: fontSize,
        letterSpacing: fontSize,
        lineHeight: fontSize,
      }),
    },
  } as const,
});

type BaseTextProps = GetProps<typeof BaseText>;

// Make typography required and make sure it only allows fontSize tokens (not numbers)
type RequireTypography<T> = Omit<
  T,
  'typography' | 'fontSize' | 'fontWeight' | 'letterSpacing' | 'lineHeight'
> & {
  /**
   * Design token that controls font size, font weight, letter spacing, and line height
   */
  typography: Exclude<BaseTextProps['typography'], number | undefined>;
  /**
   * Override the font weight of the text.
   * Note: This should be used sparingly and primarily for adjustments outside the standard typography settings.
   * The `typography` prop remains the recommended method for defining text appearance, including font weight.
   */
  fontWeight?: FontWeight;
};

export type TextProps = RequireTypography<WithoutPressableProps<BaseTextProps>> &
  Partial<AnalyticsProps>;

const isHeading = ({ tag, typography }: Partial<TextProps>) => {
  if (tag) {
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag);
  }
  // tag isn't required on mobile, so check the typography prop if tag isn't provided
  return !!typography && typography.startsWith('$header');
};

const isParagraph = ({ tag, typography }: Partial<TextProps>) => {
  if (tag) {
    return tag === 'p';
  }
  // tag isn't required on mobile, so check the typography prop if tag isn't provided
  return !!typography && typography.startsWith('$body');
};

export const getDefaultProps = (props: Partial<TextProps>) => {
  const defaultProps: Partial<TextProps> = {};

  if (isHeading(props)) {
    // Compare with: https://github.com/tamagui/tamagui/blob/master/packages/text/src/Headings.tsx
    defaultProps.accessibilityRole = 'header';
  }

  if (isParagraph(props)) {
    // Compare with: https://github.com/tamagui/tamagui/blob/master/packages/text/src/Paragraph.tsx
    defaultProps.userSelect = 'auto';
  }

  // On web, we want to use `display: block` and `display: inline` because
  // `display: flex` makes it difficult to do inline text nested inside of other
  // text. (On native, block and inline don't exist, but using flex seems to
  // work fine.)
  if (isWeb) {
    defaultProps.display = isParagraph(props) || isHeading(props) ? 'block' : 'inline';
  }

  return defaultProps;
};

/**
 * Basic component for rendering text.
 */
export const Text = forwardRef(function Text(
  { analyticsName, trackImpression = false, ...props }: TextProps,
  ref: Ref<TamaguiTextElement>,
) {
  const defaultProps = getDefaultProps(props);
  const BaseTextWithProps = <BaseText ref={ref} {...defaultProps} {...props} />;

  if (analyticsName && trackImpression) {
    return (
      <ComponentWithViewTracking
        Component={BaseTextWithProps}
        analyticsName={analyticsName}
        eventName={'textViewEvent'}
        ref={ref}
      />
    );
  }

  return BaseTextWithProps;
});

/**
 * For use in other components that need to add their own variants to Text.
 * See: https://tamagui.dev/docs/core/styled#styleable
 */
export const StyleableText = BaseText.styleable((props, ref) => <BaseText ref={ref} {...props} />);
